import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import { authNavigationGuard } from '@/routers/navigationGuards'
import {
  routeAcceptOpenDemo,
  routeAcceptSubscription,
} from '@/routers/routes/routeAcceptSubscription.ts'
import { redirectAuth, routeAuth } from '@/routers/routes/routeAuth'
import { routeMainLayoutRoot } from '@/routers/routes/routeMainLayout'
import { routeRedirect } from '@/routers/routes/routeRedirect'
import { routeResetPassword } from '@/routers/routes/routeResetPassword'
import { routeSign } from '@/routers/routes/routeSign'
import { routeTwoFactorSetup } from '@/routers/routes/routeTwoFactorSetup'
import { routeUnsubscribe } from '@/routers/routes/routeUnsubscribe'
import { routeUpdatePassword } from '@/routers/routes/routeUpdatePassword'
import { useAuthStore } from '@/stores/auth'
import { useErrorStore } from '@/stores/errorStore'
import { useIframeStore } from '@/stores/iframes/iframeStore'
import { useReportIframeStore } from '@/stores/iframes/reportIframeStore'
import { viteBaseUrl } from '@/utilities/environments'
import { handleUnknownError } from '@/utilities/errors'
import { routerRedirect } from '@/utilities/routers'
import { isNetworkErrorRedirectContentPage, NetworkError } from '@/utilities/stores'

// "catch-all route"-definition (derived from vue-router docs)
const router = createRouter({
  history: createWebHistory(viteBaseUrl),
  routes: [
    routeAuth,
    routeResetPassword,
    routeUpdatePassword,
    routeMainLayoutRoot,
    routeTwoFactorSetup,
    routeUnsubscribe,
    routeSign,
    routeAcceptSubscription,
    routeAcceptOpenDemo,
    routeRedirect,
  ],
})

// Save unsaved form data before navigating away from route
router.beforeEach(async (__, from, next) => {
  const { formId } = from.query

  // Persist dirty create-form data before leaving the current route
  if (formId) {
    const unsavedFormDataId = `${formId}`

    try {
      const reportIframeStore = useReportIframeStore()

      if (reportIframeStore.removedUnsavedFormDataIds.has(unsavedFormDataId)) {
        next()
        return
      }

      if (reportIframeStore.wrapperRef && reportIframeStore.isFormDirty) {
        const currentFormData = await reportIframeStore.getCurrentFormData()
        reportIframeStore.setUnsavedFormData(unsavedFormDataId, currentFormData)
      }
    } catch (exception) {
      console.warn(
        '[Intercept] Could not fetch/save current form data before route change:',
        exception,
      )
    }
  }

  next()
})

// set global navigation guard
// since we are using an erp framework, almost every site requires an authorization
router.beforeEach(authNavigationGuard)

// Add formId to "new-" routes if not present
router.beforeEach((to, __, next) => {
  // Only create formIds for create routes
  if (!to.matched.some((routeSegment) => routeSegment.path.includes('new-'))) {
    next()
    return
  }

  const { formId } = to.query
  const reportIframeStore = useReportIframeStore()

  // Keep the existing formId unless it was explicitly invalidated
  if (formId && !reportIframeStore.removedUnsavedFormDataIds.has(`${formId}`)) {
    next()
    return
  }

  // Regenerate the formId so each create flow can cache its own unsaved state
  const newFormId = Math.random().toString(36).substring(2, 10)
  next({
    ...to,
    query: { ...to.query, formId: newFormId },
    replace: reportIframeStore.removedUnsavedFormDataIds.has(`${formId}`),
  })
})

// When navigating to a route with formId, restore unsaved form data if present
router.afterEach((to) => {
  const errorStore = useErrorStore()
  errorStore.setRouteResolutionPending(false)

  const { formId } = to.query

  if (formId) {
    const unsavedFormDataId = `${formId}`

    try {
      const reportIframeStore = useReportIframeStore()
      const iframeStore = useIframeStore()
      const { isReady } = storeToRefs(iframeStore)

      // Replay cached field values when returning to the same create form
      const cachedFormData = reportIframeStore.getUnsavedFormData(unsavedFormDataId)

      if (cachedFormData && Object.keys(cachedFormData).length > 0) {
        const stop = watch(isReady, (ready) => {
          if (ready) {
            reportIframeStore.sendToIframe('setFieldValues', cachedFormData)
            // Stop listening once the cached values were restored
            stop()
          }
        })
      }
    } catch (exception) {
      console.warn(
        '[Form Restore] Could not fetch unsaved form data after route change:',
        exception,
      )
    }
  }
})

router.onError(async (routerError, to, from) => {
  const errorStore = useErrorStore()
  errorStore.setRouteResolutionPending(false)

  // TODO: handle different network errors
  // if error code is not expected then set error to redirect to error site
  // only authorization error handled differently
  // for now all errors have the same handling
  if (routerError instanceof NetworkError) {
    if (isNetworkErrorRedirectContentPage(routerError)) {
      const authStore = useAuthStore()
      await authStore.userSessionUpdateState()
      await routerRedirect(router, redirectAuth)
    }
  } else if (routerError instanceof Error) {
    const routerErrorPreText = 'Handle Router "onError":'
    console.error(
      routerErrorPreText,
      `\n${routerError.message}\nTo: ${to.fullPath}\nFrom: ${from.fullPath}`,
    )
  } else {
    handleUnknownError(routerError)
  }
})

export default router
